import type { UniclientComponent, UniclientWizardComponent } from '@lemonade-hq/uniclient-chat';
import { Fragment, useRef } from 'react';
import { useWizardComponents } from '../hooks/useWizardComponents';
import type { UniclientComponentAdapterProps } from './types';
import { COMPONENT_ID, COMPONENTS_MAP } from '.';

export type DynamicComponentProps = Pick<
  UniclientComponentAdapterProps,
  'answers' | 'answersSchema' | 'disabled' | 'failingValidations' | 'onAction' | 'onChange' | 'onSubmit' | 'showError'
> & {
  readonly id?: string;
  readonly component?: UniclientWizardComponent<string> | UniclientWizardComponent<string>[];
  readonly extraComponentProps?: Record<string, unknown>;
};

export const DynamicComponent: React.FC<DynamicComponentProps> = ({
  id,
  component,
  answers,
  disabled,
  extraComponentProps = {},
  ...adapterProps
}) => {
  const presentationLock = useRef<boolean>(false);

  // COMPONENTS
  const {
    isLoading: isLoadingComponents,
    components,
    componentsProps,
  } = useWizardComponents({
    componentsMap: COMPONENTS_MAP,
    currentStepComponent: component as UniclientComponent<string>[],
    presentationLock,
    answers,
  });

  presentationLock.current = isLoadingComponents;

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <Fragment key={id}>
      {components.length > 0 &&
        // eslint-disable-next-line @typescript-eslint/naming-convention
        components.map((Component, index) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (Component == null) return null;

          return (
            <Component
              {...adapterProps}
              answers={answers}
              componentProps={{ ...componentsProps[index], ...extraComponentProps }}
              disabled={isLoadingComponents || disabled}
              id={`${COMPONENT_ID}-${id}-${componentsProps[index].type}`}
              // eslint-disable-next-line react/no-array-index-key
              key={`${id}-${componentsProps[index].type}`}
            />
          );
        })}
    </Fragment>
  );
};
